import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosInterceptor';
import { AxiosError } from 'axios';
import { SessionManager } from '../../utils/SessionManager';
import { getAllUsersWithDeleted, deleteUser, restoreUser, resetUserPassword, createUser } from '../../services/userApi';
import type { User as ApiUser } from '../../services/userApi/userApi.types';
import { fetchHospitals } from '../../services/hospitalApi';
import type { Hospital as ApiHospital } from '../../services/hospitalApi/hospitalApi.types';
import { User, Hospital, UseUserManagementReturn } from './types';

// APIエラーレスポンスの型定義
interface ApiErrorResponse {
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
  [key: string]: any;
}

// APIのUser型からコンポーネントのUser型へ変換する関数
const mapApiUserToComponentUser = (apiUser: ApiUser): User => ({
  id: apiUser.id.toString(), // 常に文字列に変換
  name: apiUser.name,
  email: apiUser.email,
  role: apiUser.role as 'admin' | 'user',
  hospital_id: apiUser.hospital_id ? apiUser.hospital_id.toString() : null,
  deleted_at: apiUser.deleted_at || null,
  icon_path: apiUser.icon_path || null
});

// APIのHospital型からコンポーネントのHospital型へ変換する関数
const mapApiHospitalToComponentHospital = (apiHospital: ApiHospital): Hospital => ({
  id: apiHospital.id.toString(),
  name: apiHospital.name
});

export const useUserManagement = (currentUser: User): UseUserManagementReturn => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDeletedUsers, setShowDeletedUsers] = useState<boolean>(true);
  const [roleFilter, setRoleFilter] = useState<string>('all'); // 'all', 'admin', 'user'のいずれか
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<User | null>(null);
  const [restoreConfirmUser, setRestoreConfirmUser] = useState<User | null>(null);
  const [resetPasswordTargetUser, setResetPasswordTargetUser] = useState<User | null>(null);
  const [resetPasswordResult, setResetPasswordResult] = useState<string | null>(null);
  const [showCreateUserModal, setShowCreateUserModal] = useState<boolean>(false);
  const [createUserEmail, setCreateUserEmail] = useState<string>('');
  const [createUserName, setCreateUserName] = useState<string>('');
  const [createUserResult, setCreateUserResult] = useState<string | null>(null);
  const [isCreatingUser, setIsCreatingUser] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  
  const navigate = useNavigate();

  useEffect(() => {
    // Admin権限チェック
    if (!currentUser) {
      setLoading(false);
      setError('ユーザー情報が読み込まれていません。');
      return;
    }
    
    if (currentUser.role !== 'admin') {
      setLoading(false);
      setError('このページにアクセスする権限がありません。管理者権限が必要です。');
      return;
    }

    const activeSession = SessionManager.getActiveSession();
    if (!activeSession || !activeSession.token) {
      setLoading(false);
      setError('有効なセッションがありません。再度ログインしてください。');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const [apiUserData, apiHospitalData] = await Promise.all([
          getAllUsersWithDeleted(true),
          fetchHospitals()
        ]);
        
        // API型からコンポーネント型へ変換
        const userData = apiUserData.map(mapApiUserToComponentUser);
        const hospitalData = apiHospitalData.map(mapApiHospitalToComponentHospital);
        
        // 管理者の病院IDを取得
        const adminHospitalId = currentUser.hospital_id;
        
        // スーパー管理者（病院IDなし）はすべてのユーザーを見られる
        // 病院管理者は自分の病院のユーザーのみ見られる
        // hospital_id は string/number が混在し得るため、比較は文字列に正規化する。
        // currentUser 側は toUserEntity が Number() 化する一方、user 側はマッパーが
        // 文字列化するため、=== では型が食い違って常に不一致になる。
        //
        // 注: 本番の hospital_id は UUID のため Number(uuid) = NaN → falsy となり、
        // この三項自体が不成立（＝絞り込まず全件表示）になる。つまり本修正が実効を
        // 持つのは数値文字列IDを使うデモ環境。クライアント側の絞り込みはセキュリティ
        // 境界ではなく、病院スコープの担保はサーバ側の責務。
        const filteredUserData = adminHospitalId
          ? userData.filter((user) => String(user.hospital_id) === String(adminHospitalId))
          : userData;
          
        setUsers(filteredUserData);
        setFilteredUsers(filteredUserData);
        setHospitals(hospitalData);
        setError(null);
      } catch (error: unknown) {
        console.error('データ取得エラー:', error);
        
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError<ApiErrorResponse>;
          if (axiosError.response) {
            setError(`エラー: ${axiosError.response.status} - ${axiosError.response.data?.message || '不明なエラー'}`);
          } else if (axiosError.request) {
            setError('サーバーからの応答がありません。ネットワーク接続を確認してください。');
          } else {
            setError(`エラーが発生しました: ${axiosError.message}`);
          }
        } else {
          const err = error as Error;
          setError(`エラーが発生しました: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  // フィルタリング
  useEffect(() => {
    let result = users;
    
    // 検索語でフィルタリング
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(user => 
        user.name.toLowerCase().includes(term) || 
        user.email.toLowerCase().includes(term)
      );
    }
    
    // 削除済みユーザーのフィルタリング
    if (!showDeletedUsers) {
      result = result.filter(user => !user.deleted_at);
    }
    
    // ロールでフィルタリング
    if (roleFilter !== 'all') {
      result = result.filter(user => user.role === roleFilter);
    }
    
    setFilteredUsers(result);
  }, [users, searchTerm, showDeletedUsers, roleFilter]);

  // 病院名を取得するヘルパー関数
  const getHospitalName = (hospitalId: string | null): string => {
    if (!hospitalId) return '未設定';
    const hospital = hospitals.find(h => String(h.id) === String(hospitalId));
    return hospital ? hospital.name : '不明';
  };

  // ユーザーの役割を更新する関数
  const handleRoleChange = async (userId: string, newRole: 'admin' | 'user'): Promise<void> => {
    try {
      // セッション取得
      const activeSession = SessionManager.getActiveSession();
      if (!activeSession || !activeSession.token) {
        setError('有効なセッションがありません。再度ログインしてください。');
        return;
      }
      
      // ヘッダーに明示的にトークンを設定
      await axios.put(`/api/users/${userId}`,
        { role: newRole },
        {
          headers: {
            Authorization: `Bearer ${activeSession.token}`
          }
        }
      );
      
      // 成功したら状態を更新
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, role: newRole } : user
        )
      );
    } catch (err: unknown) {
      console.error('Failed to update user role:', err);
      
      // エラーメッセージをより詳細に
      if (axios.isAxiosError(err)) {
        const axiosError = err as AxiosError<ApiErrorResponse>;
        if (axiosError.response) {
          if (axiosError.response.status === 401) {
            setError('認証に失敗しました。再度ログインしてください。');
          } else if (axiosError.response.status === 403) {
            setError('ユーザー権限を変更する権限がありません。');
          } else {
            setError(`ユーザー権限の更新に失敗しました: ${axiosError.response.data?.error || '不明なエラー'}`);
          }
        } else {
          setError('サーバーに接続できません。ネットワーク接続を確認してください。');
        }
      } else {
        const error = err as Error;
        setError(`エラーが発生しました: ${error.message}`);
      }
    }
  };

  // ユーザー削除処理
  const handleDeleteUser = async (userId: string): Promise<void> => {
    try {
      await deleteUser(userId);
      
      // 成功したらユーザーリストを更新
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, deleted_at: new Date().toISOString() } : user
        )
      );
      
      // 成功メッセージを表示
      setSuccessMessage('ユーザーを削除しました');
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 3000);
      
      // 削除確認モーダルを閉じる
      setDeleteConfirmUser(null);
    } catch (err: unknown) {
      console.error('Failed to delete user:', err);
      setError('ユーザーの削除に失敗しました');
    }
  };
  
  // ユーザー復元処理
  const handleRestoreUser = async (userId: string): Promise<void> => {
    try {
      await restoreUser(userId);
      
      // 成功したらユーザーリストを更新
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, deleted_at: null } : user
        )
      );
      
      // 成功メッセージを表示
      setSuccessMessage('ユーザーを復元しました');
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 3000);
      
      // 復元確認モーダルを閉じる
      setRestoreConfirmUser(null);
    } catch (err: unknown) {
      console.error('Failed to restore user:', err);
      setError('ユーザーの復元に失敗しました');
    }
  };

  const handleResetUserPassword = async (userId: string): Promise<void> => {
    try {
      const response = await resetUserPassword(userId);
      setResetPasswordResult(response.temporaryPassword);
    } catch (err: unknown) {
      setError('パスワードのリセットに失敗しました');
      setResetPasswordTargetUser(null);
    }
  };

  const handleCreateUser = async (): Promise<void> => {
    if (!createUserEmail.trim()) return;
    setIsCreatingUser(true);
    setCreateUserResult(null);
    try {
      const response = await createUser(createUserEmail.trim(), createUserName.trim() || undefined);
      setCreateUserResult(response.temporaryPassword);
      // ユーザーリストに追加
      const newUser: User = {
        id: response.user.id,
        email: response.user.email,
        name: response.user.name ?? response.user.email,
        role: response.user.role as 'admin' | 'user',
        hospital_id: response.user.hospital_id,
        deleted_at: null,
        icon_path: null,
      };
      setUsers(prev => [newUser, ...prev]);
    } catch (err: unknown) {
      setError('ユーザーの作成に失敗しました');
    } finally {
      setIsCreatingUser(false);
    }
  };

  // 日付フォーマット関数
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '未設定';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ja-JP', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // 現在の病院名を取得
  const currentHospitalName = currentUser.hospital_id 
    ? getHospitalName(currentUser.hospital_id) 
    : 'すべての病院';

  return {
    users,
    filteredUsers,
    resetPasswordTargetUser,
    resetPasswordResult,
    showCreateUserModal,
    createUserEmail,
    createUserName,
    createUserResult,
    isCreatingUser,
    setResetPasswordTargetUser: (user) => {
      setResetPasswordTargetUser(user);
      setResetPasswordResult(null);
    },
    setShowCreateUserModal: (show) => {
      setShowCreateUserModal(show);
      if (!show) {
        setCreateUserEmail('');
        setCreateUserName('');
        setCreateUserResult(null);
      }
    },
    setCreateUserEmail,
    setCreateUserName,
    handleCreateUser,
    handleResetUserPassword,
    hospitals,
    loading,
    error,
    searchTerm,
    showDeletedUsers,
    roleFilter,
    deleteConfirmUser,
    restoreConfirmUser,
    isSuccess,
    successMessage,
    currentHospitalName,
    setSearchTerm,
    setShowDeletedUsers,
    setRoleFilter,
    setDeleteConfirmUser,
    setRestoreConfirmUser,
    handleDeleteUser,
    handleRestoreUser,
    handleRoleChange,
    formatDate,
    getHospitalName
  };
}; 